import { useCallback } from 'react';
import { useAlertContext } from '../../common/form';

import { ValidationRuleValidDataType } from '../schema';
import { useValidateValidationRules } from './query/useValidateValidationRules';
import { useMutationErrors } from '../../common/queryClient';
import translations from './useCheckValidationRule.translations.json';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import { GetApiErrorMessage } from '../../common/utils/ValidationMessagesMap';

const { t, addTranslations, useTranslation } = i18nNsFactory(
  'use-check-validation-rule',
);
addTranslations(translations);

const translateMutationError: GetApiErrorMessage = (code, meta) =>
  t('alert.error.code.' + code, meta?.args && { ...meta.args });

export const useCheckValidationRule = (
  validationRule?: Partial<ValidationRuleValidDataType>,
) => {
  const { t } = useTranslation();
  const { setSuccess } = useAlertContext();
  const { mutationErrors, setMutationErrors, clearMutationErrors } =
    useMutationErrors(translateMutationError);
  const { mutateAsync, isPending } = useValidateValidationRules();
  const handleCheck = useCallback(async () => {
    clearMutationErrors();
    if (!validationRule) return;

    const validationRuleId = validationRule.id;
    if (!validationRuleId || validationRuleId < 0) return;

    const versionId = validationRule.dataObjectDescriptionVersionId;
    if (!versionId || versionId < 0) return;

    return await mutateAsync({
      descriptionVersionId: versionId,
      validationRuleId,
    })
      .then(() => {
        setSuccess(t('alert.validation.success'));
      })
      .catch((error) => {
        setMutationErrors(error?.response?.data?.errors ?? []);
      });
  }, [
    clearMutationErrors,
    mutateAsync,
    setMutationErrors,
    setSuccess,
    t,
    validationRule,
  ]);

  return { handleCheck, mutationErrors, isChecking: isPending };
};
